import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import { SearchUser } from "@/types/resources";
import { SearchUsersResponse } from "@/types/responses";

const MIN_QUERY_LENGTH = 3;

export const useSearchUsers = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchUser[]>([]);

  const client = useAxiosClient("/sharing");

  const run = useCallback(
    (query: string) => {
      client
        .get<SearchUsersResponse>("/search", { params: { email: query } })
        .then(({ data }) => {
          setResults(data.users);
        })
        .catch(() => {
          setResults([]);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client]
  );

  const debounced = useMemo(() => _.debounce(run, 300), [run]);

  useEffect(() => () => debounced.cancel(), [debounced]);

  const search = useCallback(
    (query: string) => {
      if (query.trim().length < MIN_QUERY_LENGTH) {
        debounced.cancel();
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      debounced(query);
    },
    [debounced]
  );

  const clear = useCallback(() => {
    debounced.cancel();
    setResults([]);
    setLoading(false);
  }, [debounced]);

  return { clear, loading, results, search };
};
